import type { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, RefreshCw, FolderPlus, Pencil, Sparkles } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import type { Source } from '@/types';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DataTableColumnHeader } from '@/components/ui/data-table';
import { SourceStatusBadge } from '@/modules/sources/components/source-status-badge';
import { SourceTypeIcon } from './source-type-icon';
import { CategorizeMissHint } from '@/components/shared/categorize-miss-hint';

// Resync re-runs ingestion for a source: FILE/LINK/ARTICLE just re-process the already-stored
// content, INTEGRATION Docs re-fetch the live doc and diff before re-ingesting. Google Sheets
// integrations don't support it on the backend, so they're the one exclusion.
function canResyncSource(source: Source): boolean {
  if (source.type === 'INTEGRATION') return !source.value.includes('/spreadsheets/d/');
  return true;
}

interface SourceColumnsOptions {
  onDelete: (source: Source) => void;
  canDelete?: boolean;
  onResync?: (source: Source) => void;
  resyncingSourceIds?: Set<string>;
  onRename?: (source: Source) => void;
  canRename?: boolean;
  onAddToCategory?: (source: Source) => void;
  canAddToCategory?: boolean;
  /** Opens the source's Promote knowledge drawer; absent for those who cannot see promotion. */
  onPromoteKnowledge?: (source: Source) => void;
}

export function getSourceColumns({
  onDelete,
  canDelete = true,
  onResync,
  resyncingSourceIds,
  onRename,
  canRename = true,
  onAddToCategory,
  canAddToCategory = true,
  onPromoteKnowledge
}: SourceColumnsOptions): ColumnDef<Source>[] {
  return [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Name' />,
      cell: ({ row }) => {
        const type = row.original.type;
        const name = row.getValue('name') as string;
        return (
          <div className='flex items-center gap-2 min-w-0'>
            <SourceTypeIcon type={type} className='size-4 text-foreground shrink-0' />
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='font-medium truncate block min-w-0'>{name}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{name}</p>
              </TooltipContent>
            </Tooltip>
            <CategorizeMissHint miss={row.original.categorizeMiss} />
          </div>
        );
      },
      size: 650 // 55% width
    },
    {
      accessorKey: 'updatedAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Last Synced' />,
      cell: ({ row }) => {
        const date = new Date(row.getValue('updatedAt'));
        return (
          <div className='flex items-center gap-2'>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='text-xs text-muted-foreground cursor-default'>
                    {formatDistanceToNow(date, { addSuffix: true })}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{format(date, 'PPpp zzz')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      size: 225 // ~22.5% width (half of the remaining 45%)
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Status' />,
      cell: ({ row }) => {
        const status = row.getValue('status') as 'SUCCESS' | 'FAILED' | 'INPROGRESS';
        return <SourceStatusBadge status={status} />;
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
      size: 75 // ~22.5% width (half of the remaining 45%)
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const source = row.original;

        const isSyncing = source.status === 'INPROGRESS' || (resyncingSourceIds?.has(source.id) ?? false);
        const canShowResync = !!onResync && canResyncSource(source);
        if (!canDelete && !canAddToCategory && !canRename && !canShowResync && !onPromoteKnowledge) return null;
        return (
          <div className='flex items-center justify-center' onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size={'icon'} className='h-8 w-8 p-0'>
                  <span className='sr-only'>Open menu</span>
                  <MoreHorizontal className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {onPromoteKnowledge && (
                  <DropdownMenuItem onClick={() => onPromoteKnowledge(source)}>
                    <Sparkles className='h-4 w-4 mr-2' />
                    Promote knowledge
                  </DropdownMenuItem>
                )}
                {canAddToCategory && (
                  <DropdownMenuItem onClick={() => onAddToCategory?.(source)}>
                    <FolderPlus className='h-4 w-4 mr-2' />
                    Add to category
                  </DropdownMenuItem>
                )}
                {canShowResync && (
                  <DropdownMenuItem onClick={() => onResync?.(source)} disabled={isSyncing}>
                    <RefreshCw className='h-4 w-4 mr-2' />
                    Re-sync
                  </DropdownMenuItem>
                )}
                {canRename && (
                  <DropdownMenuItem onClick={() => onRename?.(source)}>
                    <Pencil className='h-4 w-4 mr-2' />
                    Rename
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <DropdownMenuItem onClick={() => onDelete(source)} className='text-destructive focus:text-destructive'>
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 60,
      maxSize: 60
    }
  ];
}
